"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";

/**
 * Thin gradient bar fixed at the top of the viewport. Slides in on any
 * client-side navigation, hides once the new route has rendered.
 *
 * Strategy:
 *  - Intercept clicks on <a> elements that look like internal navigation
 *    and start the bar immediately (the click happens before Next's
 *    pathname change so we can't rely on usePathname effects alone).
 *  - When usePathname/useSearchParams update — we know the new route's
 *    server segment resolved — finish the bar.
 *  - Auto-time-out after 8s so a stuck SSR can't leave the bar hanging.
 */
export function RouteProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function start() {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setVisible(true);
    setProgress(8);
    // Ease toward 80% so the user feels constant motion. The final 20%
    // is reserved for the actual route resolution.
    let value = 8;
    intervalRef.current = setInterval(() => {
      value = value + Math.max(0.4, (80 - value) * 0.08);
      if (value > 80) value = 80;
      setProgress(value);
    }, 80);
    // Safety timeout — never let the bar hang forever.
    timeoutRef.current = setTimeout(() => finish(), 8000);
  }

  function finish() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setProgress(100);
    // Allow the 100% width to render before fading out.
    setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 260);
  }

  // Start the bar on any internal-link click.
  useEffect(() => {
    function isInternal(anchor: HTMLAnchorElement) {
      if (!anchor.href) return false;
      if (anchor.target && anchor.target !== "_self") return false;
      try {
        const url = new URL(anchor.href);
        if (url.origin !== window.location.origin) return false;
        // Same URL = no navigation
        if (
          url.pathname === window.location.pathname &&
          url.search === window.location.search &&
          url.hash !== ""
        ) {
          return false;
        }
        return true;
      } catch {
        return false;
      }
    }
    function onClick(e: MouseEvent) {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const path = e.composedPath?.() ?? [];
      const anchor = path.find((el): el is HTMLAnchorElement => el instanceof HTMLAnchorElement);
      if (!anchor || !isInternal(anchor)) return;
      start();
    }
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Finish whenever the route settles to a new pathname/search.
  useEffect(() => {
    if (!visible) return;
    finish();
    // intentionally excluding 'visible' to only react to nav updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  return (
    <div
      aria-hidden
      className="fixed top-0 left-0 right-0 z-[100] pointer-events-none h-[2px]"
      style={{ opacity: visible ? 1 : 0, transition: "opacity 220ms ease" }}
    >
      <div
        className="h-full rounded-r-full"
        style={{
          width: `${progress}%`,
          background: "linear-gradient(90deg, var(--color-primary), var(--color-accent))",
          boxShadow: "0 0 8px color-mix(in srgb, var(--color-accent) 60%, transparent)",
          transition: "width 180ms ease-out",
        }}
      />
    </div>
  );
}
